import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormsModule, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { dropdownAnimation } from '@vex/animations/dropdown.animation';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { DecimalPipe, NgFor, NgIf } from '@angular/common';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MailAccount, MailService } from '../../services/mail.service';
import { Mail } from '../../interfaces/mail.interface';
import { forkJoin, of } from 'rxjs';
import { AdminService } from 'src/app/services/admin.service';

interface RecipientSuggestion {
  name: string;
  email: string;
  initials: string;
  caption: string;
}

export interface ComposeDraft {
  id?: number;
  uid?: string;
  messageid?: string;
  to?: string;
  cc?: string;
  bcc?: string;
  subject?: string;
  body_html?: string;
  body_text?: string;
  files?: File[];
  openCcBcc?: boolean;
  fromMailSource?: string;
  fromMailExpression?: string;
  fromEmail?: string;
  fromMailPassword?: string;
  displayName?: string;
  companyid?: number;
  userid?: number;
}

@Component({
    selector: 'vex-mail-compose',
    templateUrl: './mail-compose.component.html',
    styleUrls: ['./mail-compose.component.scss'],
    animations: [dropdownAnimation],
    imports: [
        MatButtonModule,
        MatIconModule,
        MatFormFieldModule,
        MatInputModule,
        MatTooltipModule,
        ReactiveFormsModule,
        FormsModule,
        NgIf,
        NgFor,
        DecimalPipe,
        MatProgressBarModule
    ]
})
export class MailComposeComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('editor') editor?: ElementRef<HTMLDivElement>;

  @Input() account!: MailAccount | null;
  @Input() mode: 'compose' | 'reply' | 'replyAll' | 'forward' = 'compose';
  @Input() mail?: Mail;
  @Input() draft?: ComposeDraft | null;
  @Input() storageKey = '';
  @Input() minimized = false;
  @Input() maximized = false;
  @Output() close = new EventEmitter<void>();
  @Output() discard = new EventEmitter<void>();
  @Output() minimizeChange = new EventEmitter<boolean>();
  @Output() maximizeChange = new EventEmitter<boolean>();

  dropdownOpen = false;
  sending = false;
  savingDraft = false;
  forwardingAttachments = false;
  files: File[] = [];
  toDraft = '';
  ccDraft = '';
  bccDraft = '';
  toRecipients: string[] = [];
  ccRecipients: string[] = [];
  bccRecipients: string[] = [];
  activeRecipientField: RecipientField | null = null;
  highlightedSuggestionIndex: Record<RecipientField, number> = {
    to: 0,
    cc: 0,
    bcc: 0
  };
  editing: { field: RecipientField; index: number } | null = null;
  editValue = '';
  draftStatus = '';
  private lastSavedDraftJson = '';
  private draftLoadedFromStorage = false;
  private selectingSuggestionFor: RecipientField | null = null;
  private shouldPersistOnDestroy = true;
  private draftId = 0;
  private draftUid = '';
  private draftMessageId = '';

  form = this.fb.group({
    to: ['', [Validators.required, this.emailListValidator.bind(this)]],
    cc: ['', this.emailListValidator.bind(this)],
    bcc: ['', this.emailListValidator.bind(this)],
    subject: ['', Validators.required],
    body_html: ['']
  });

  constructor(
    private fb: FormBuilder,
    private mailService: MailService,
    private adminService: AdminService,
    private snackBar: MatSnackBar
  ) {}

  get matchedTemplateAccount(): MailAccount | null {
    const fromEmail = (this.draft?.fromEmail || '').trim().toLowerCase();
    if (!fromEmail) {
      return null;
    }

    return this.mailService.accounts.value.find(
      (account) => (account.emailaddress || '').trim().toLowerCase() === fromEmail
    ) || null;
  }

  get composeAccount() {
    return this.account || this.matchedTemplateAccount;
  }

  get effectiveFromEmail() {
    return (this.draft?.fromEmail || this.composeAccount?.emailaddress || '').trim();
  }

  get isTemplateDraft() {
    return !!this.draft?.fromEmail && !!this.draft?.fromMailPassword;
  }

  get shouldUseMailboxFlow() {
    return !!this.composeAccount && (!this.isTemplateDraft || !!this.matchedTemplateAccount);
  }

  ngOnInit(): void {
    this.patchByMode();
    this.syncDraftIdentity();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const onlyAccountChanged =
      !!changes['account'] &&
      !changes['mail'] &&
      !changes['mode'] &&
      !changes['draft'];

    if (onlyAccountChanged) {
      return;
    }

    if (changes['mail'] || changes['mode'] || changes['draft'] || changes['account']) {
      this.patchByMode();
    }
  }

  ngOnDestroy(): void {
    if (this.shouldPersistOnDestroy) {
      this.persistDraftSnapshot('Draft saved');
    }
  }

  get title() {
    if (this.mode === 'compose' && (this.draftId > 0 || Number(this.draft?.id || 0) > 0)) {
      return 'Draft';
    }
    if (this.mode === 'reply') {
      return 'Reply';
    }
    if (this.mode === 'replyAll') {
      return 'Reply all';
    }
    if (this.mode === 'forward') {
      return 'Forward';
    }
    return 'New Message';
  }

  toggleDropdown() {
    this.dropdownOpen = !this.dropdownOpen;
  }

  requestClose() {
    if (this.savingDraft || this.sending) {
      return;
    }

    this.shouldPersistOnDestroy = true;
    this.persistDraftSnapshot('Draft saved');
    if (this.mode !== 'compose' || !this.composeAccount?.id) {
      this.close.emit();
      return;
    }

    void this.saveDraftAndClose();
  }

  requestDiscard() {
    if (this.savingDraft || this.sending) {
      return;
    }

    this.shouldPersistOnDestroy = false;
    void this.discardDraftAndClose();
  }

  toggleMinimize() {
    this.persistDraftSnapshot('Draft saved');
    this.minimizeChange.emit(!this.minimized);
  }

  toggleMaximize() {
    this.persistDraftSnapshot('Draft saved');
    if (this.minimized) {
      this.minimizeChange.emit(false);
    }
    this.maximizeChange.emit(!this.maximized);
  }

  attach(files: FileList | null) {
    if (!files) {
      return;
    }
    this.files = [...this.files, ...Array.from(files)];
  }

  removeFile(index: number) {
    this.files.splice(index, 1);
  }

  async submit() {
    this.flushRecipientDrafts();
    this.onEditorInput();
    if (this.form.invalid || (!this.shouldUseMailboxFlow && !this.isTemplateDraft)) {
      this.form.markAllAsTouched();
      return;
    }

    this.sending = true;
    if (this.isTemplateDraft && !this.matchedTemplateAccount) {
      try {
        const attachments = await Promise.all(
          this.files.map(async (file) => ({
            filename: file.name,
            fileByte: await this.fileToBase64(file),
            size: String(file.size || 0),
            FilePath: '',
            filePath: ''
          }))
        );

        this.adminService.sendTemplateMail({
          companyid: Number(this.draft?.companyid || this.adminService.getsessioncompanyid() || 0),
          userid: Number(this.draft?.userid || 0),
          fromEmail: this.draft?.fromEmail || '',
          fromPassword: this.draft?.fromMailPassword || '',
          displayName: this.draft?.displayName || this.draft?.fromEmail || '',
          subject: this.form.value.subject || '',
          content: this.form.value.body_html || '',
          toAddresses: this.mapAddresses(this.form.value.to || ''),
          ccAddresses: this.mapAddresses(this.form.value.cc || ''),
          bccAddresses: this.mapAddresses(this.form.value.bcc || ''),
          replyToAddresses: {
            name: this.draft?.displayName || this.draft?.fromEmail || '',
            address: this.draft?.fromEmail || ''
          },
          emailAttachments: attachments
        }).subscribe({
          next: () => this.finishSendSuccess(),
          error: (error) => {
            this.sending = false;
            this.snackBar.open(error?.error?.message || 'Template mail send failed.', 'Close', { duration: 4000 });
          }
        });
        return;
      } catch {
        this.sending = false;
        this.snackBar.open('Attachment processing failed.', 'Close', { duration: 3500 });
        return;
      }
    }

    this.mailService
      .sendMail({
        configid: this.composeAccount!.id,
        to: this.form.value.to || '',
        cc: this.form.value.cc || '',
        bcc: this.form.value.bcc || '',
        subject: this.form.value.subject || '',
        body_html: this.form.value.body_html || '',
        body_text: this.stripHtml(this.form.value.body_html || ''),
        files: this.files
      })
      .subscribe({
        next: () => this.finishSendSuccess(),
        error: () => (this.sending = false)
      });
  }

  private patchByMode() {
    const signatureHtml = this.getSignatureHtml();
    this.files = [];
    this.forwardingAttachments = false;

    if (this.mode === 'compose') {
      const draft = this.resolveComposeDraft(signatureHtml);
      const to = this.parseRecipientList(draft?.to);
      const cc = this.parseRecipientList(draft?.cc);
      const bcc = this.parseRecipientList(draft?.bcc);
      const bodyHtml = draft?.body_html || this.buildComposeBody(signatureHtml);

      this.dropdownOpen = !!draft?.openCcBcc || cc.length > 0 || bcc.length > 0;
      this.setRecipients('to', []);
      this.setRecipients('cc', []);
      this.setRecipients('bcc', []);
      this.setRecipients('to', to);
      this.setRecipients('cc', cc);
      this.setRecipients('bcc', bcc);
      this.form.patchValue({
        subject: draft?.subject || '',
        body_html: bodyHtml
      });
      this.files = [...(draft?.files || [])];
      this.setEditorHtml(bodyHtml);
      this.syncDraftIdentity(draft);
      return;
    }

    if (!this.mail) {
      return;
    }

    const quotedBody = this.buildQuotedBody(this.mail, signatureHtml);

    if (this.mode === 'reply') {
      this.dropdownOpen = false;
      this.setRecipients('to', [this.mail.from.email]);
      this.setRecipients('cc', []);
      this.setRecipients('bcc', []);
      this.form.patchValue({
        subject: `Re: ${this.cleanSubject(this.mail.subject)}`,
        body_html: quotedBody
      });
      this.setEditorHtml(quotedBody);
    }

    if (this.mode === 'replyAll') {
      const currentEmail = this.composeAccount?.emailaddress || '';
      const originalTo = (this.mail.addresses || []).filter((a) => a.type === 'TO').map((a) => a.email);
      const originalCc = (this.mail.addresses || []).filter((a) => a.type === 'CC').map((a) => a.email);
      const to = this.uniqueEmails([this.mail.from.email, ...originalTo], currentEmail);
      const cc = this.uniqueEmails(originalCc, currentEmail);
      this.dropdownOpen = cc.length > 0;
      this.setRecipients('to', to);
      this.setRecipients('cc', cc);
      this.setRecipients('bcc', []);
      this.form.patchValue({
        subject: `Re: ${this.cleanSubject(this.mail.subject)}`,
        body_html: quotedBody
      });
      this.setEditorHtml(quotedBody);
    }

    if (this.mode === 'forward') {
      this.dropdownOpen = false;
      this.setRecipients('to', []);
      this.setRecipients('cc', []);
      this.setRecipients('bcc', []);
      const forwardBody = this.buildForwardBody(this.mail, signatureHtml);
      this.form.patchValue({
        subject: `Fw: ${this.cleanSubject(this.mail.subject)}`,
        body_html: forwardBody
      });
      this.setEditorHtml(forwardBody);
      this.loadForwardAttachments();
    }
  }

  private loadForwardAttachments() {
    if (!this.mail?.attachments?.length) {
      return;
    }

    const attachmentRequests = this.mail.attachments.map((attachment) =>
      attachment.id ? this.mailService.fetchAttachmentFile(attachment) : of(null)
    );

    this.forwardingAttachments = true;
    forkJoin(attachmentRequests).subscribe({
      next: (files) => {
        this.files = files.filter((file): file is File => !!file);
        this.forwardingAttachments = false;
      },
      error: () => {
        this.files = [];
        this.forwardingAttachments = false;
      }
    });
  }

  private cleanSubject(subject: string) {
    return (subject || '').replace(/^(re|fw):\s*/i, '');
  }

  private stripHtml(value: string) {
    const element = document.createElement('div');
    element.innerHTML = value;
    return element.textContent || element.innerText || '';
  }

  private parseRecipientList(value?: string | null) {
    return this.uniqueEmails(
      String(value || '')
        .split(/[,;\n]+/)
        .map((email) => email.trim())
        .filter(Boolean)
    );
  }

  private uniqueEmails(emails: string[], excludeEmail = '') {
    const exclude = excludeEmail.trim().toLowerCase();
    const seen = new Set<string>();
    return emails
      .map((email) => (email || '').trim())
      .filter((email) => {
        const key = email.toLowerCase();
        if (!key || key === exclude || seen.has(key)) {
          return false;
        }
        seen.add(key);
        return true;
      });
  }

  private buildComposeBody(signatureHtml: string) {
    return `<p><br></p>${signatureHtml}`;
  }

  private buildQuotedBody(mail: Mail, signatureHtml: string) {
    return `<p><br></p>${signatureHtml}<p><br></p><blockquote>${mail.body || ''}</blockquote>`;
  }

  private buildForwardBody(mail: Mail, signatureHtml: string) {
    const from = mail.from?.email || '';
    const date = mail.date ? new Date(mail.date).toLocaleString() : '';
    return `<p><br></p>${signatureHtml}<p><br></p><blockquote><p><b>From:</b> ${from}</p><p><b>Date:</b> ${date}</p><p><b>Subject:</b> ${mail.subject || ''}</p><br>${mail.body || ''}</blockquote>`;
  }

  onRecipientKeydown(event: KeyboardEvent, field: RecipientField) {
    this.activeRecipientField = field;
    const draft = this.getDraft(field).trim();
    const suggestions = this.suggestedRecipients(field);

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (!draft || !suggestions.length) {
        return;
      }

      event.preventDefault();
      const direction = event.key === 'ArrowDown' ? 1 : -1;
      const nextIndex = this.highlightedSuggestionIndex[field] + direction;
      this.highlightedSuggestionIndex[field] = (nextIndex + suggestions.length) % suggestions.length;
      return;
    }

    if (event.key === 'Backspace') {
      const recipients = this.getRecipients(field);
      if (!draft && recipients.length) {
        event.preventDefault();
        this.removeRecipient(field, recipients.length - 1);
      }
      return;
    }

    if (event.key === 'Escape') {
      this.activeRecipientField = null;
      return;
    }

    if (event.key !== 'Enter' && event.key !== ',' && event.key !== 'Tab') {
      return;
    }

    if (event.key === 'Tab' && !draft) {
      return;
    }

    if (!draft && !suggestions.length) {
      return;
    }

    event.preventDefault();

    if (draft && suggestions.length && !this.isValidEmail(draft)) {
      const index = this.getSafeSuggestionIndex(field, suggestions);
      this.addSuggestedRecipient(field, suggestions[index]);
      return;
    }

    this.addRecipient(field);
  }

  onRecipientPaste(event: ClipboardEvent, field: RecipientField) {
    this.activeRecipientField = field;
    const text = event.clipboardData?.getData('text') || '';
    if (!text.includes(',') && !text.includes(';') && !text.includes('\n')) {
      return;
    }

    event.preventDefault();
    this.addRecipient(field, text);
  }

  addRecipient(field: RecipientField, rawValue = this.getDraft(field)) {
    const values = rawValue
      .split(/[,;\n]+/)
      .map((value) => value.trim())
      .filter(Boolean);

    if (!values.length) {
      return;
    }

    const recipientsToAdd = values.filter((value) => this.isValidEmail(value));
    if (!recipientsToAdd.length) {
      return;
    }

    this.setRecipients(field, this.uniqueEmails([...this.getRecipients(field), ...recipientsToAdd]));
    this.setDraft(field, '');
    this.activeRecipientField = field;
    this.highlightedSuggestionIndex[field] = 0;
  }

  removeRecipient(field: RecipientField, index: number) {
    this.setRecipients(
      field,
      this.getRecipients(field).filter((_, recipientIndex) => recipientIndex !== index)
    );
  }

  startEditRecipient(field: RecipientField, index: number) {
    this.editing = { field, index };
    this.editValue = this.getRecipients(field)[index] || '';
  }

  commitRecipientEdit() {
    if (!this.editing) {
      return;
    }

    const recipients = [...this.getRecipients(this.editing.field)];
    const value = this.editValue.trim();
    if (value) {
      recipients[this.editing.index] = value;
    } else {
      recipients.splice(this.editing.index, 1);
    }

    this.setRecipients(this.editing.field, this.uniqueEmails(recipients));
    this.editing = null;
    this.editValue = '';
  }

  isEditing(field: RecipientField, index: number) {
    return this.editing?.field === field && this.editing.index === index;
  }

  isInvalidEmail(email: string) {
    return !this.isValidEmail(email);
  }

  suggestedRecipients(field: RecipientField): RecipientSuggestion[] {
    const currentEmail = (this.composeAccount?.emailaddress || '').trim().toLowerCase();
    const selected = new Set(this.getRecipients(field).map((email) => email.trim().toLowerCase()));
    const draft = this.getDraft(field).trim().toLowerCase();
    const suggestions = new Map<string, RecipientSuggestion>();
    const pushSuggestion = (email: string, name = '') => {
      this.expandAddresses(email, name).forEach((entry) => {
        const normalizedEmail = entry.email.trim();
        const key = normalizedEmail.toLowerCase();
        if (!key || key === currentEmail || selected.has(key) || suggestions.has(key)) {
          return;
        }

        const displayName = entry.name || this.emailName(normalizedEmail);
        const haystack = `${displayName} ${normalizedEmail}`.toLowerCase();
        if (draft && !haystack.includes(draft)) {
          return;
        }

        suggestions.set(key, {
          name: displayName,
          email: normalizedEmail,
          initials: this.initialsFor(displayName || normalizedEmail),
          caption: this.suggestionCaption(field)
        });
      });
    };

    (this.mail?.addresses || []).forEach((item) => pushSuggestion(item.email || '', item.name || ''));
    pushSuggestion(this.mail?.from?.email || '', this.mail?.from?.name || '');
    pushSuggestion(this.mail?.to?.email || '', this.mail?.to?.name || '');

    this.mailService.getSuggestionSeedMails().forEach((item) => {
      pushSuggestion(item.from?.email || '', item.from?.name || '');
      pushSuggestion(item.to?.email || '', item.to?.name || '');
      (item.addresses || []).forEach((address) => pushSuggestion(address.email || '', address.name || ''));
    });

    return Array.from(suggestions.values()).slice(0, 8);
  }

  addSuggestedRecipient(field: RecipientField, suggestion: RecipientSuggestion) {
    this.setRecipients(field, this.uniqueEmails([...this.getRecipients(field), suggestion.email], this.composeAccount?.emailaddress || ''));
    this.setDraft(field, '');
    this.activeRecipientField = field;
    this.highlightedSuggestionIndex[field] = 0;
    this.persistDraftSnapshot('Draft saved');
  }

  selectSuggestedRecipient(event: MouseEvent, field: RecipientField, suggestion: RecipientSuggestion) {
    event.preventDefault();
    event.stopPropagation();
    this.selectingSuggestionFor = field;
    this.addSuggestedRecipient(field, suggestion);
    queueMicrotask(() => {
      if (this.selectingSuggestionFor === field) {
        this.selectingSuggestionFor = null;
      }
    });
  }

  onSuggestionPointerDown(event: PointerEvent, field: RecipientField, suggestion: RecipientSuggestion) {
    if (this.savingDraft || this.sending) {
      return;
    }

    this.selectingSuggestionFor = field;
    event.preventDefault();
    event.stopPropagation();
    this.addSuggestedRecipient(field, suggestion);
    setTimeout(() => {
      if (this.selectingSuggestionFor === field) {
        this.selectingSuggestionFor = null;
      }
    }, 0);
  }

  onRecipientBlur(field: RecipientField) {
    setTimeout(() => {
      if (this.selectingSuggestionFor === field) {
        return;
      }

      this.addRecipient(field);
      this.closeSuggestions(field);
    }, 0);
  }

  onCcBccToggle() {
    this.dropdownOpen = !this.dropdownOpen;
    if (this.dropdownOpen) {
      this.activeRecipientField = 'cc';
    } else if (this.activeRecipientField === 'cc' || this.activeRecipientField === 'bcc') {
      this.activeRecipientField = null;
    }
  }

  showSuggestionList(field: RecipientField) {
    return this.activeRecipientField === field && !!this.getDraft(field).trim() && this.suggestedRecipients(field).length > 0;
  }

  activateRecipientField(field: RecipientField) {
    this.activeRecipientField = field;
    this.highlightedSuggestionIndex[field] = 0;
  }

  closeSuggestions(field: RecipientField) {
    if (this.activeRecipientField !== field) {
      return;
    }

    setTimeout(() => {
      if (this.activeRecipientField === field) {
        this.activeRecipientField = null;
      }
    }, 150);
  }

  private flushRecipientDrafts() {
    this.addRecipient('to');
    this.addRecipient('cc');
    this.addRecipient('bcc');
  }

  private getRecipients(field: RecipientField) {
    if (field === 'to') {
      return this.toRecipients;
    }
    if (field === 'cc') {
      return this.ccRecipients;
    }
    return this.bccRecipients;
  }

  private setRecipients(field: RecipientField, values: string[]) {
    if (field === 'to') {
      this.toRecipients = values;
    } else if (field === 'cc') {
      this.ccRecipients = values;
    } else {
      this.bccRecipients = values;
    }

    this.form.get(field)?.setValue(values.join(', '));
    this.form.get(field)?.markAsDirty();
    this.form.get(field)?.updateValueAndValidity({ emitEvent: false });
  }

  private getDraft(field: RecipientField) {
    if (field === 'to') {
      return this.toDraft;
    }
    if (field === 'cc') {
      return this.ccDraft;
    }
    return this.bccDraft;
  }

  private setDraft(field: RecipientField, value: string) {
    if (field === 'to') {
      this.toDraft = value;
    } else if (field === 'cc') {
      this.ccDraft = value;
    } else {
      this.bccDraft = value;
    }

    this.highlightedSuggestionIndex[field] = 0;
  }

  isSuggestionHighlighted(field: RecipientField, index: number) {
    return this.highlightedSuggestionIndex[field] === index;
  }

  private emailListValidator(control: AbstractControl): ValidationErrors | null {
    const value = String(control.value || '').trim();
    if (!value) {
      return null;
    }

    const invalid = value.split(',').map((email) => email.trim()).filter(Boolean).some((email) => !this.isValidEmail(email));
    return invalid ? { emailList: true } : null;
  }

  private isValidEmail(email: string) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  }

  private emailName(email: string) {
    return (email || '')
      .split('@')[0]
      .replace(/[._-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private initialsFor(value: string) {
    const parts = (value || '')
      .split(/\s+/)
      .map((part) => part.trim())
      .filter(Boolean);
    return (parts.slice(0, 2).map((part) => part.charAt(0)).join('') || value.charAt(0) || '?').toUpperCase();
  }

  private suggestionCaption(field: RecipientField) {
    if (field === 'cc') {
      return 'Add to Cc';
    }
    if (field === 'bcc') {
      return 'Add to Bcc';
    }
    return 'Add to To';
  }

  private expandAddresses(value: string, name = '') {
    const parsedEmails = String(value || '')
      .split(/[,;\n]+/)
      .map((item) => item.trim())
      .filter((item) => this.isValidEmail(item));

    if (!parsedEmails.length) {
      return [];
    }

    if (parsedEmails.length === 1) {
      return [{ email: parsedEmails[0], name: (name || '').trim() }];
    }

    return parsedEmails.map((email) => ({
      email,
      name: ''
    }));
  }

  private getSafeSuggestionIndex(field: RecipientField, suggestions: RecipientSuggestion[]) {
    if (!suggestions.length) {
      return 0;
    }

    const index = this.highlightedSuggestionIndex[field] || 0;
    if (index < 0 || index >= suggestions.length) {
      return 0;
    }

    return index;
  }

  focusEditor() {
    this.editor?.nativeElement.focus();
  }

  onEditorInput() {
    this.form.get('body_html')?.setValue(this.editor?.nativeElement.innerHTML || '');
  }

  format(command: string, value?: string) {
    this.focusEditor();
    document.execCommand(command, false, value);
    this.onEditorInput();
  }

  setFontSize(value: string) {
    if (!value) {
      return;
    }
    this.format('fontSize', value);
  }

  createLink() {
    const url = window.prompt('Enter link URL');
    if (!url) {
      return;
    }
    this.format('createLink', url);
  }

  insertInlineImage(files: FileList | null) {
    const file = files?.[0];
    if (!file) {
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      this.focusEditor();
      document.execCommand('insertImage', false, String(reader.result || ''));
      this.onEditorInput();
    };
    reader.readAsDataURL(file);
  }

  private setEditorHtml(value: string) {
    queueMicrotask(() => {
      if (this.editor?.nativeElement) {
        this.editor.nativeElement.innerHTML = value || '';
        this.placeCaretAtStart();
      }
    });
  }

  private getSignatureHtml() {
    if (!this.composeAccount?.signature_enabled || !this.composeAccount?.signature_html) {
      return '';
    }

    return `<div class="mail-signature">${this.composeAccount.signature_html}</div>`;
  }

  private placeCaretAtStart() {
    const editor = this.editor?.nativeElement;
    if (!editor) {
      return;
    }

    editor.focus();
    const selection = window.getSelection();
    if (!selection) {
      return;
    }

    const range = document.createRange();
    range.selectNodeContents(editor);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
  }

  private mapAddresses(value: string) {
    return this.parseRecipientList(value).map((address) => ({
      name: this.emailName(address),
      address
    }));
  }

  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = String(reader.result || '');
        const base64 = result.includes(',') ? result.split(',')[1] : result;
        resolve(base64);
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  }

  private persistDraftSnapshot(statusText = 'Draft saved') {
    if (this.mode !== 'compose' || !this.storageKey) {
      return;
    }

    const snapshot = this.createDraftSnapshot();
    const snapshotJson = JSON.stringify(snapshot);
    if (!snapshotJson || snapshotJson === this.lastSavedDraftJson) {
      return;
    }

    sessionStorage.setItem(this.storageKey, snapshotJson);
    this.lastSavedDraftJson = snapshotJson;
    this.draftStatus = statusText;
  }

  private createDraftSnapshot(): ComposeDraft {
    this.flushRecipientDrafts();
    this.onEditorInput();
    return {
      ...this.draft,
      id: this.draftId || this.draft?.id,
      uid: this.draftUid || this.draft?.uid,
      messageid: this.draftMessageId || this.draft?.messageid,
      to: this.toRecipients.join(', '),
      cc: this.ccRecipients.join(', '),
      bcc: this.bccRecipients.join(', '),
      subject: this.form.value.subject || '',
      body_html: this.form.value.body_html || '',
      openCcBcc: this.dropdownOpen
    };
  }

  private resolveComposeDraft(signatureHtml: string): ComposeDraft | null {
    if (!this.draftLoadedFromStorage) {
      const storedDraft = this.getStoredDraft();
      if (storedDraft) {
        this.draftLoadedFromStorage = true;
        this.lastSavedDraftJson = JSON.stringify(storedDraft);
        this.draftStatus = 'Draft restored';
        return {
          ...this.draft,
          ...storedDraft,
          files: this.draft?.files || storedDraft.files || []
        };
      }
    }

    return this.draft || {
      body_html: this.buildComposeBody(signatureHtml)
    };
  }

  private getStoredDraft(): ComposeDraft | null {
    if (!this.storageKey) {
      return null;
    }

    try {
      const raw = sessionStorage.getItem(this.storageKey);
      return raw ? (JSON.parse(raw) as ComposeDraft) : null;
    } catch {
      return null;
    }
  }

  private clearStoredDraft() {
    if (!this.storageKey) {
      return;
    }

    sessionStorage.removeItem(this.storageKey);
    this.lastSavedDraftJson = '';
    this.draftStatus = '';
  }

  private syncDraftIdentity(draft?: ComposeDraft | null) {
    const source = draft || this.draft || null;
    this.draftId = Number(source?.id || 0);
    this.draftUid = String(source?.uid || '');
    this.draftMessageId = String(source?.messageid || '');
  }

  private async saveDraftAndClose() {
    this.flushRecipientDrafts();
    this.onEditorInput();
    this.savingDraft = true;
    this.draftStatus = 'Saving draft...';

    this.mailService.saveDraft({
      id: this.draftId,
      configid: this.composeAccount!.id,
      uid: this.draftUid,
      messageid: this.draftMessageId,
      to: this.form.value.to || '',
      cc: this.form.value.cc || '',
      bcc: this.form.value.bcc || '',
      subject: this.form.value.subject || '',
      body_html: this.form.value.body_html || '',
      body_text: this.stripHtml(this.form.value.body_html || ''),
      files: this.files
    }).subscribe({
      next: (res) => {
        const savedId = Number(res?.data?.id || res?.id || 0);
        if (savedId > 0) {
          this.draftId = savedId;
        }
        this.persistDraftSnapshot('Draft saved');
        this.savingDraft = false;
        this.close.emit();
      },
      error: (error) => {
        this.savingDraft = false;
        this.draftStatus = 'Draft save failed';
        this.snackBar.open(error?.error?.message || 'Draft save failed.', 'Close', { duration: 3500 });
      }
    });
  }

  private async discardDraftAndClose() {
    this.clearStoredDraft();

    if (!this.draftId) {
      this.discard.emit();
      return;
    }

    this.savingDraft = true;
    this.draftStatus = 'Deleting draft...';
    this.mailService.deleteDraft(this.draftId).subscribe({
      next: () => {
        this.savingDraft = false;
        this.draftId = 0;
        this.discard.emit();
      },
      error: (error) => {
        this.savingDraft = false;
        this.draftStatus = 'Draft delete failed';
        this.snackBar.open(error?.error?.message || 'Draft delete failed.', 'Close', { duration: 3500 });
      }
    });
  }

  private finishSendSuccess() {
    this.clearStoredDraft();

    if (!this.draftId) {
      this.sending = false;
      this.close.emit();
      return;
    }

    const draftId = this.draftId;
    this.mailService.deleteDraft(draftId).subscribe({
      next: () => {
        this.sending = false;
        this.draftId = 0;
        this.close.emit();
      },
      error: () => {
        this.sending = false;
        this.draftId = 0;
        this.close.emit();
      }
    });
  }
}

type RecipientField = 'to' | 'cc' | 'bcc';
